import React from 'react';
import { Box } from 'src/design/atoms/Box';
import { Loader } from 'src/design/atoms/Loader';
import { Text } from 'src/design/atoms/Text';
import { Tabs } from 'src/design/molecules/Tabs';
import { useNavigate, useParams } from 'react-router-dom';
import { DrawingDto, DrawingHistoryDto } from 'src/api/FiretrolApiClient';
import { useDrawing } from 'src/api/queries/drawings/useDrawingQueries';
import { useJobs } from 'src/hooks/useJobs';
import {
  DrawingForm,
  DrawingFormSection,
} from 'src/routes/Jobs/Drawings/components/DrawingForm';
import { HistoryList } from 'src/app/workflows/HistoryList';
import { NotificationsTab } from 'src/app/workflows/NotificationsTab';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';

export const ViewDrawing: React.FC = () => {
  const params = useParams<{ jobId: string; drawingId: string }>();
  const navigate = useNavigate();
  const { selectedJob, users } = useJobs();
  const [tab, setTab] = React.useState<DrawingFormSection>('details');
  const { data: drawing, isLoading: loading } = useDrawing(
    selectedJob?.id,
    params.drawingId,
  );
  const drawingHistory = drawing?.history ?? [];

  if (loading) return <Loader />;
  if (!drawing || !selectedJob) return <Text>Unable to find drawing</Text>;

  const jobUsers = users ?? [];

  return (
    <>
      <Header
        breadcrumbProps={{
          items: jobBreadcrumbs(
            selectedJob,
            { text: 'Drawings', href: `/jobs/${selectedJob.id}/drawings` },
            drawing.drawingNumber || 'No Drawing Number',
          ),
        }}
        title={drawing.drawingNumber || 'View Drawing'}
        helpText="View and update this drawing's details, attachments, and notifications."
        actions={
          <SecondaryButton
            onClick={() => navigate(`/jobs/${selectedJob.id}/drawings`)}
          >
            Cancel
          </SecondaryButton>
        }
      />

      <Tabs
        value={tab}
        onChange={(value) => setTab((value as DrawingFormSection) || 'details')}
        mt="md"
      >
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="attachments">Attachments</Tabs.Tab>
          <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Box mt="md">
        <DrawingForm
          initialValues={drawing}
          selectedJob={selectedJob}
          activeSection={tab}
          jobUsers={jobUsers}
          onCreated={(d) =>
            navigate(`/jobs/${selectedJob.id}/drawings/${d.id}`)
          }
        >
          {tab === 'notifications' && (
            <NotificationsTab
              resourceType="drawing"
              entityNoun="Drawing"
              attachPdfLabel="Attach Drawing PDF"
              jobId={selectedJob.id}
              entityId={drawing.id}
              jobUsers={jobUsers}
              customerNumber={selectedJob.customerNumber?.trim() ?? ''}
            />
          )}

          {tab === 'history' && <HistoryList history={drawingHistory} />}
        </DrawingForm>
      </Box>
    </>
  );
};
